import { createApi } from '@reduxjs/toolkit/query/react'
import { httpRequest } from '@calendly/http-tools'

const httpRequestBaseQuery = async (arg) => {
  const [success, responseData, response] = await httpRequest(arg)

  if (success) {
    return { data: responseData, meta: { response } }
  }

  return { error: responseData }
}

export const rootApi = createApi({
  baseQuery: httpRequestBaseQuery,
  endpoints: (build) => ({
    getExperimenterConfig: build.query({
      query: ({ organizationUuid, locale, userId, anonymousId }) => {
        const params = { organization_uuid: organizationUuid, locale }
        if (userId) params.user_id = userId
        if (anonymousId) params.anonymous_id = anonymousId

        return {
          url: '/api/booking/experiments',
          params
        }
      }
    }),
    getInitialSettings: build.query({
      query: () => {
        const urlParams = new URLSearchParams(window.location.search)
        const locale = urlParams.get('locale')
        const params = {}
        if (locale) params.locale = locale

        return {
          url: '/api/booking/initial_settings',
          params
        }
      },
      transformResponse: (response, meta) => {
        const headers = meta?.response?.headers
        if (headers) {
          const countryCode = headers['x-country-code'] || 'US'
          const gdprApplied = headers['x-gdpr-applied'] ? headers['x-gdpr-applied'] === 'true' : false

          response.request.country_code = countryCode
          response.request.gdpr_applied = gdprApplied
        } else {
          response.request.country_code = 'US'
          response.request.gdpr_applied = false
        }

        return response
      }
    }),
    getProfile: build.query({
      query: ({ slug }) => {
        const urlParams = new URLSearchParams(window.location.search)
        const optionalParams = ['embed_domain', 'embed_type', 'hide_gdpr_banner']

        const params = {}

        optionalParams.forEach((param) => {
          const value = urlParams.get(param)
          if (value) params[param] = value
        })

        return {
          url: `/api/booking/profiles/${slug}`,
          params
        }
      },
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    getSchedulingLink: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/scheduling_links/${uuid}` }),
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    getPurchasedPackage: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/purchased_packages/${uuid}` }),
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    getFeatures: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/features/${uuid}` }),
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    getInvitee: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/invitees/${uuid}` }),
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    confirmInvitee: build.mutation({
      query: ({ uuid }) => ({
        url: `/api/booking/invitee_confirmations/${uuid}`,
        method: 'PATCH'
      })
    }),
    getRescheduledInvitee: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/invitees/${uuid}` }),
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    getEventType: build.query({
      query: ({ profileSlug, slug, shareUuid, eventTypeUuid }) => {
        const urlParams = new URLSearchParams(window.location.search)
        const optionalParams = ['embed_domain', 'embed_type', 'hide_gdpr_banner']

        const params = {
          event_type_slug: slug,
          profile_slug: profileSlug,
          share_uuid: shareUuid,
          ...(eventTypeUuid && { event_type_uuid: eventTypeUuid })
        }

        optionalParams.forEach((param) => {
          const value = urlParams.get(param)
          if (value) params[param] = value
        })

        return {
          url: '/api/booking/event_types/lookup',
          params
        }
      },
      serializeQueryArgs: ({ endpointName }) => endpointName
    }),
    getEventTypes: build.query({
      query: ({ slug }) => ({ url: `/api/booking/profiles/${slug}/event_types` })
    }),
    getPublicPackages: build.query({
      query: ({ slug }) => ({ url: `/api/booking/profiles/${slug}/packages` })
    }),
    getRoutingForm: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/routing_forms/${uuid}` })
    }),
    getLandingPage: build.query({
      query: ({ uuid }) => ({ url: `/api/booking/landing_pages/${uuid}` })
    }),
    getProfileLandingPage: build.query({
      query: ({ slug }) => ({ url: `/api/booking/profiles/${slug}/landing_page` })
    }),
    getCalendarAccounts: build.query({
      query: () => ({ url: '/api/booking/calendar_accounts' })
    }),
    validateCoupon: build.mutation({
      query: ({ code, inviteeEmail, eventTypeUuid, clientSecret }) => ({
        url: '/api/booking/booking_codes/apply',
        method: 'POST',
        data: {
          code,
          invitee_email: inviteeEmail,
          event_type_uuid: eventTypeUuid,
          payment_intent_client_secret: clientSecret
        }
      })
    })
  })
})

export const eventTypeSelector = (state) => (
  rootApi.endpoints.getEventType.select()(state).data
)

export const inviteeSelector = (state) => (
  rootApi.endpoints.getInvitee.select()(state).data
)

export const rescheduledInviteeSelector = (state) => (
  rootApi.endpoints.getRescheduledInvitee.select()(state).data
)

export const schedulingLinkSelector = (state) => (
  rootApi.endpoints.getSchedulingLink.select()(state).data
)

export const purchasedPackageSelector = (state) => (
  rootApi.endpoints.getPurchasedPackage.select()(state).data?.resource
)

export const requestSelector = (state) => (
  rootApi.endpoints.getInitialSettings.select()(state).data.request
)

export const sessionSelector = (state) => (
  rootApi.endpoints.getInitialSettings.select()(state).data.session
)

export const featuresSelector = (state) => (
  rootApi.endpoints.getFeatures.select()(state).data
)

export const {
  useGetExperimenterConfigQuery,
  useGetCalendarAccountsQuery,
  useGetInitialSettingsQuery,
  useGetProfileQuery,
  useGetInviteeQuery,
  useConfirmInviteeMutation,
  useGetRescheduledInviteeQuery,
  useGetFeaturesQuery,
  useGetSchedulingLinkQuery,
  useGetPurchasedPackageQuery,
  useGetEventTypeQuery,
  useGetEventTypesQuery,
  useGetPublicPackagesQuery,
  useGetProfileLandingPageQuery,
  useGetLandingPageQuery,
  useGetRoutingFormQuery,
  useValidateCouponMutation
} = rootApi

export const {
  getCalendarAccounts
} = rootApi.endpoints

export const setEventType = (attributes) => (
  rootApi.util.updateQueryData('getEventType', undefined, (state) => ({ ...state, ...attributes }))
)

export const setInvitee = (attributes) => (
  rootApi.util.upsertQueryData('getInvitee', undefined, attributes)
)
